/**
 * Raising a task (`KLQ-20260821-001`, Scope: "Creating a task") — one drawer, two entrances.
 *
 * From the plain register the location starts empty; from the node-narrowed register the
 * active `under=` node is pre-filled, which is what "creating a task from a hierarchy node"
 * means on this screen — the node the person is already looking at is the node the task is
 * about, and asking them to find it again in a picker would be the screen forgetting what it
 * was just showing.
 *
 * THREE RULES THIS FILE HOLDS TO
 *
 * 1. **The pin inputs appear only where the cached tree says the node is a drawing** — a
 *    container has no sheet to pin to. But the tree is a cache, and the rule itself lives in
 *    `domains/tasks/service.py:_place`: whatever this drawer sends, the refusal is the
 *    server's `422` with its own sentence, rendered here rather than prevented by a guess. A
 *    half-filled pin is deliberately *sent* (as the not-a-number the wire reads as missing)
 *    so the person gets the server's "needs normalised x and y" instead of a silently
 *    unpinned task.
 * 2. **An admission is stated before the act that would perform it** (§F). Assigning the new
 *    task to somebody whose firm is not the project's owner lets that firm in the moment the
 *    task exists. TWIN: `TaskDetail.tsx`'s `admissionFor`, minus its audience check — a task
 *    that does not exist yet has admitted nobody, so there is no list to consult. No
 *    interposed confirm here, unlike the detail: there the select fires the `PATCH` at once,
 *    while here nothing happens until *Create*, so the statement stands above the button and
 *    pressing it is the confirmation.
 * 3. **Nothing here decides who may do what.** The server refuses an assignment the assignee
 *    could not open, a node outside the caller's zones, a label cap — each as its own
 *    sentence, printed via `detailOf` like every other refusal on this screen.
 */

import { useState } from "react";

import { useQuery } from "@tanstack/react-query";

import {
  Button,
  Drawer,
  InlineError,
  Input,
  Select,
  TreePicker,
} from "@/components";
import type { TreeTableNode } from "@/components";
import { hierarchyQueryOptions } from "@/features/drawings";
import { UploadError } from "@/features/drawings/upload";
import { membersQueryOptions } from "@/features/people";
import { projectQueryOptions } from "@/features/projects";
import { useActiveWorkspaceId } from "@/features/session";
import type { MessageKeys } from "@/lib/i18n";
import { useMessages } from "@/lib/i18n";

import type {
  TaskCreate,
  TaskKind,
  TaskPriority,
  WorkspaceTaskType,
} from "./api";
import { TASK_KINDS, TASK_PRIORITIES } from "./api";
import { dueAtEndOfDay } from "./filters";
import { KIND_KEY } from "./kinds";
import { LabelPicker } from "./LabelPicker";
import { attachFailureKey, detailOf, taskErrorKey } from "./problems";
import {
  offerableTypes,
  useAttachToNewTask,
  useCreateTask,
  useTaskTypes,
} from "./queries";
import { TASK_TYPE_ICONS } from "./taskTypeIcons";
import styles from "./Tasks.module.css";

type Key = MessageKeys["tasks"];

/** `TaskCreateRequest` field bounds, from `domains/tasks/schemas.py`. TWIN: that file. */
const TITLE_MAX = 200;
const DESCRIPTION_MAX = 10_000;

const PRIORITY_KEY: Readonly<Record<TaskPriority, Key>> = {
  high: "priority.high",
  normal: "priority.normal",
  low: "priority.low",
};

/**
 * The shape the location picker needs from a hierarchy node row.
 *
 * Structural rather than imported: the hierarchy payload carries more than this, and the
 * drawer cares about exactly these five facts.
 */
interface NodeRow {
  readonly id: string;
  readonly name: string;
  readonly code?: string | null;
  readonly kind: string;
  readonly parent_id?: string | null;
  readonly archived_at?: string | null;
}

interface LocationOption {
  readonly id: string;
  readonly name: string;
  //: `{code}`'s substitution, and `applies_to`'s filter input. Both are properties of the
  //: node the task will hang on, which is why they travel with the option rather than being
  //: looked up again from the raw rows.
  readonly code: string | null;
  readonly kind: string;
  readonly drawing: boolean;
}

/** The type's own glyph and colour beside the sentence explaining what it filled in. */
function TypeHint({
  type,
  label,
}: {
  readonly type: WorkspaceTaskType;
  readonly label: string;
}) {
  const Glyph = TASK_TYPE_ICONS[type.icon];
  return (
    <span className={styles.fieldHint}>
      <Glyph size={14} aria-hidden />
      {label}
    </span>
  );
}

/**
 * A type's `title_template`, resolved against the node the task will sit on.
 *
 * The two tokens are the server's (`_TEMPLATE_TOKENS`: `label` and `code`) and this is a
 * second implementation of them, deliberately — **the server expands no template on a
 * hand-made create** (`KLQ-20260825-005` §6). A batch has no human at the keyboard to correct
 * a title; a single create has nothing else, so the drawer fills the field in and the person
 * can overwrite it before submitting.
 *
 * With no node chosen the placeholders are dropped rather than left visible, and the
 * separator they hung off goes with them: `Vypracovať pôdorys — {label}` prefills as
 * `Vypracovať pôdorys`. Choosing a node afterwards re-resolves it, while the field is clean.
 */
function resolveTemplate(
  template: string,
  node: LocationOption | undefined,
): string {
  const filled = template.replace(
    /\{(label|code)\}/g,
    (_match, token: string) => {
      if (node === undefined) return "";
      return token === "label" ? node.name : (node.code ?? "");
    },
  );
  return filled
    .replace(/\s*[—–\-:]\s*$/u, "")
    .replace(/\s+/gu, " ")
    .trim();
}

/**
 * Every place a task can sit — containers *and* drawings, unlike the slicer rail's
 * containers-only list (`plan.ts`, `containerOptions`), because a task is filed on either
 * while `under=` only slices by subtree. One walk over the raw node rows: the hierarchy payload already carries the slot
 * rows (they share their primary key with the node they specialise), so this costs no second
 * request and no join with the slots endpoint. Order within a parent is the server's
 * `display_order`, preserved; archived branches are the server's history, not a filing target.
 *
 * A tree rather than a flat list since `KLQ-20260905-002` §B: the depth used to be a field on
 * the option because a `<select>` could only show it as leading spaces, and `TreePicker`
 * reads the nesting itself.
 */
function locationNodes(
  nodes: readonly NodeRow[],
): TreeTableNode<LocationOption>[] {
  const byParent = new Map<string | null, NodeRow[]>();
  for (const node of nodes) {
    if (node.archived_at != null || node.kind === "project") continue;
    const key = node.parent_id ?? null;
    const bucket = byParent.get(key);
    if (bucket === undefined) byParent.set(key, [node]);
    else bucket.push(node);
  }

  const root = nodes.find((node) => node.kind === "project");
  const seen = new Set<string>();
  const walk = (parentId: string | null): TreeTableNode<LocationOption>[] => {
    const out: TreeTableNode<LocationOption>[] = [];
    for (const node of byParent.get(parentId) ?? []) {
      // A parent cycle would be a server defect; refusing to re-walk a node keeps it from
      // becoming a frozen tab (the `useNodeTrail` reasoning).
      if (seen.has(node.id)) continue;
      seen.add(node.id);
      out.push({
        id: node.id,
        row: {
          id: node.id,
          name: node.name,
          code: node.code ?? null,
          kind: node.kind,
          drawing: node.kind === "slot",
        },
        children: walk(node.id),
      });
    }
    return out;
  };
  return walk(root?.id ?? null);
}

/** One place out of that tree, by id — the two lookups the drawer's own logic needs. */
function findLocation(
  nodes: readonly TreeTableNode<LocationOption>[],
  id: string,
): LocationOption | undefined {
  for (const node of nodes) {
    if (node.id === id) return node.row;
    const found = findLocation(node.children ?? [], id);
    if (found !== undefined) return found;
  }
  return undefined;
}

/**
 * A pin coordinate as the wire wants it: the person types a percentage, the API reads 0…1.
 *
 * An empty field is `NaN` on purpose, never `0` — `Number('')` is `0`, and a pin silently
 * invented at the sheet's corner is exactly the guess rule 1 forbids. `NaN` serialises as
 * JSON `null`, which the server refuses with the sentence naming the missing coordinate.
 */
function pct(value: string): number {
  return value.trim() === "" ? Number.NaN : Number(value) / 100;
}

/**
 * The page a pin sits on: 1-based on screen, zero-based on the wire.
 *
 * People count sheets from one and the viewer's page control does too; `TaskAnchor.page` is
 * zero-based because `CoordinateAnchor.page` is, and one representation across the two pin
 * kinds is worth more than matching the label. An empty field is the first page, which is the
 * answer for the single-page drawings that are most of them — unlike `pct`, where an empty
 * field is a missing coordinate rather than a default, because a pin invented at the sheet's
 * corner is a guess and a pin on page one is what the person is looking at.
 */
function pageIndex(value: string): number {
  const typed = Number(value.trim());
  return value.trim() === "" || !Number.isFinite(typed)
    ? 0
    : Math.max(0, Math.trunc(typed) - 1);
}

export interface TaskCreateDrawerProps {
  readonly projectId: string;
  /** The hierarchy node the register is narrowed to, when it is — the pre-filled entrance. */
  readonly locationId: string | null;
  readonly onClose: () => void;
}

export function TaskCreateDrawer({
  projectId,
  locationId,
  onClose,
}: TaskCreateDrawerProps) {
  const t = useMessages("tasks");
  const create = useCreateTask(projectId);
  const tree = useQuery(hierarchyQueryOptions(projectId));
  const members = useQuery(membersQueryOptions(projectId));
  const project = useQuery(projectQueryOptions(projectId));
  const myWorkspaceId = useActiveWorkspaceId();

  const [kind, setKind] = useState<TaskKind>("task");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState<TaskPriority>("normal");
  const [assignee, setAssignee] = useState("");
  const [dueDay, setDueDay] = useState("");
  const [nodeId, setNodeId] = useState(locationId ?? "");
  const [anchorX, setAnchorX] = useState("");
  const [anchorY, setAnchorY] = useState("");
  const [anchorPage, setAnchorPage] = useState("");
  const [labels, setLabels] = useState<readonly string[]>([]);
  const [verification, setVerification] = useState(false);
  const [titleError, setTitleError] = useState<Key | null>(null);
  const [taskTypeId, setTaskTypeId] = useState("");
  //: Set on the first keystroke in the title field, and never cleared. It is what stops a
  //: change of type from eating a sentence somebody wrote.
  const [titleDirty, setTitleDirty] = useState(false);
  // The photo, and the idempotency key minted when it was picked. The key belongs to *one
  // chosen file*: held for as long as that file is the chosen one, replaced when a different
  // file takes its place (`features/drawings/upload.ts` states the contract).
  const [photo, setPhoto] = useState<{
    readonly file: File;
    readonly key: string;
  } | null>(null);
  const [photoFraction, setPhotoFraction] = useState<number | null>(null);
  const attachToNew = useAttachToNewTask(projectId);
  // Set when the task was written and its photo was not. From then on the drawer never creates
  // again: the primary action retries the photo against this task, and the fields are read-only
  // because they describe a record that already exists.
  const [created, setCreated] = useState<{ readonly id: string } | null>(null);
  const busy = create.isPending || attachToNew.isPending;
  const locked = busy || created !== null;

  const options = locationNodes(tree.data?.nodes ?? []);
  const location = findLocation(options, nodeId);

  // No fetch of its own: `useTaskTypes` is the cache the register, the board, the detail and
  // the generate drawer already fill, keyed by workspace. `offerableTypes` is the only place
  // archived rows are dropped (`KLQ-20260825-005` §5) and stays so.
  const taskTypes = useTaskTypes();
  const offerable = offerableTypes(taskTypes.data?.items);
  // `applies_to` filters the picker and refuses nothing — a studio raising a *Skontrolovať*
  // on a floor is not making a mistake the server can see. An empty list means any kind.
  const offered = offerable.filter(
    (type) =>
      type.applies_to.length === 0 ||
      location === undefined ||
      type.applies_to.includes(location.kind),
  );
  const picked = offered.find((type) => type.id === taskTypeId);
  // The type's own rule, from §4: a studio that says somebody has to confirm this kind of
  // work is not overridden by the person raising one instance of it. The override only ever
  // turns verification *on*.
  const verificationForced = picked?.requires_verification ?? false;

  function applyType(type: WorkspaceTaskType | undefined): void {
    setTaskTypeId(type?.id ?? "");
    if (type === undefined) return;
    setKind(type.kind);
    setPriority(type.priority);
    if (type.requires_verification) setVerification(true);
    if (!titleDirty) {
      setTitle(resolveTemplate(type.title_template, location));
      setTitleError(null);
    }
  }

  function relocate(nextNodeId: string): void {
    setNodeId(nextNodeId);
    // The title re-resolves against the new node while the field is still clean — picking the
    // type before the place is the ordinary order, and the prefill would otherwise be stuck
    // with the placeholders dropped.
    if (picked !== undefined && !titleDirty) {
      const next = findLocation(options, nextNodeId);
      setTitle(resolveTemplate(picked.title_template, next));
    }
  }
  // Undefined for the empty choice *and* for a pre-filled node the cached tree does not hold
  // (pruned, or removed since the link was shared) — in both cases no pin is offered, because
  // "is this a drawing?" has no honest answer here. The node id itself still goes to the
  // server, whose 404/422 is the answer.
  const drawing = location?.drawing ?? false;

  // Rule 2: whether this assignment would let a firm in, from facts the server already gave
  // this caller — and silence where it cannot know (an external caller cannot name the owner;
  // a member list the caller may not read yields no firms at all, §G).
  const owningWorkspaceId =
    project.data?.relation === "owned" ? myWorkspaceId : null;
  const member = members.data?.items.find((row) => row.user_id === assignee);
  const admittedFirmId = member?.company_workspace_id ?? null;
  const admission =
    owningWorkspaceId !== null &&
    member !== undefined &&
    admittedFirmId !== null &&
    admittedFirmId !== owningWorkspaceId
      ? {
          personName:
            member.display_name ?? member.email ?? t("person.unavailable"),
          firmName: member.company_name ?? t("audience.firmUnknown"),
        }
      : null;

  function body(): TaskCreate {
    const due = dueAtEndOfDay(dueDay);
    const trimmedDescription = description.trim();
    return {
      title: title.trim(),
      kind,
      priority,
      requires_verification: verification,
      ...(trimmedDescription === "" ? {} : { description: trimmedDescription }),
      // A one-element list on a wire that takes N: the single control is `KLQ-20260824-001`
      // decision 3's convention — a task raised here (the register's entrance, and the
      // detail's subtask entrance) starts with one accountable person or none, and the
      // multi-select lives on the detail where the set is edited.
      ...(assignee === "" ? {} : { assignee_user_ids: [assignee] }),
      ...(due === null ? {} : { due_at: due }),
      ...(nodeId === "" ? {} : { hierarchy_node_id: nodeId }),
      // Sent when the person touched either field (rule 1): a complete pin is the pin, a
      // half-filled one earns the server's own sentence about the missing half.
      ...(drawing && (anchorX.trim() !== "" || anchorY.trim() !== "")
        ? {
            anchor: {
              x: pct(anchorX),
              y: pct(anchorY),
              page: pageIndex(anchorPage),
            },
          }
        : {}),
      ...(labels.length === 0 ? {} : { label_ids: [...labels] }),
      // Clearing the picker is a legitimate answer, so the field is absent rather than null:
      // an untyped task stays legal, and it must — every task written before types existed is
      // one.
      ...(taskTypeId === "" ? {} : { task_type_id: taskTypeId }),
    };
  }

  function attachPhoto(
    taskId: string,
    chosen: { readonly file: File; readonly key: string },
  ): void {
    attachToNew.mutate(
      {
        taskId,
        file: chosen.file,
        idempotencyKey: chosen.key,
        onProgress: (progress) => {
          setPhotoFraction(progress.fraction);
        },
      },
      {
        onSuccess: () => {
          onClose();
        },
        // The task is written and stays written. The drawer stays open and says so, because a
        // closed drawer would leave nobody to read the error — and a task silently missing the
        // photo it was raised with is exactly the evidence a contractor's issue exists to carry.
        onError: () => {
          setCreated({ id: taskId });
          setPhotoFraction(null);
        },
      },
    );
  }

  function submit(): void {
    if (busy) return;
    if (created !== null) {
      if (photo === null) {
        onClose();
        return;
      }
      attachPhoto(created.id, photo);
      return;
    }
    if (title.trim() === "") {
      setTitleError("create.error.titleRequired");
      return;
    }
    setTitleError(null);
    create.mutate(body(), {
      onSuccess: (task) => {
        if (photo === null) {
          onClose();
          return;
        }
        attachPhoto(task.id, photo);
      },
    });
  }

  return (
    <Drawer
      title={t("create.title")}
      closeLabel={t("create.close")}
      onClose={onClose}
      footer={
        <>
          <Button variant="tertiary" disabled={busy} onClick={onClose}>
            {created === null ? t("label.cancel") : t("create.close")}
          </Button>
          <Button variant="primary" loading={busy} onClick={submit}>
            {created === null ? t("create.submit") : t("create.photoRetry")}
          </Button>
        </>
      }
    >
      {create.isError ? (
        <InlineError variant="block">
          {detailOf(create.error) ?? t(taskErrorKey(create.error))}
        </InlineError>
      ) : null}

      <form
        className={styles.createForm}
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <div className={styles.field}>
          <Select
            label={t("filter.kind")}
            value={kind}
            disabled={locked}
            onChange={(event) => {
              setKind(event.target.value as TaskKind);
            }}
          >
            {TASK_KINDS.map((value) => (
              <option key={value} value={value}>
                {t(KIND_KEY[value])}
              </option>
            ))}
          </Select>
        </div>

        <div className={styles.field}>
          <Select
            label={t("create.typeLabel")}
            value={taskTypeId}
            disabled={locked || taskTypes.isLoading}
            onChange={(event) => {
              applyType(offered.find((type) => type.id === event.target.value));
            }}
          >
            {/* A task has one type or none — unlike the generate drawer's multi-select, which
                exists because a batch writes one task per (node x type). */}
            <option value="">{t("create.typeNone")}</option>
            {offered.map((type) => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </Select>
          {picked === undefined ? null : (
            <TypeHint type={picked} label={t("create.typeApplied")} />
          )}
        </div>

        <Input
          label={t("detail.titleLabel")}
          autoFocus
          maxLength={TITLE_MAX}
          value={title}
          disabled={locked}
          error={titleError === null ? undefined : t(titleError)}
          onChange={(event) => {
            setTitle(event.target.value);
            setTitleDirty(true);
            setTitleError(null);
          }}
        />

        <label className={styles.field}>
          <span className={styles.fieldLabel}>{t("detail.description")}</span>
          <textarea
            className={styles.textarea}
            rows={4}
            maxLength={DESCRIPTION_MAX}
            value={description}
            disabled={locked}
            onChange={(event) => {
              setDescription(event.target.value);
            }}
          />
        </label>

        <div className={styles.field}>
          <span className={styles.fieldLabel}>{t("create.photo")}</span>
          <input
            type="file"
            accept="image/*"
            className={styles.photoInput}
            disabled={busy}
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = "";
              attachToNew.reset();
              if (file === undefined) {
                setPhoto(null);
                return;
              }
              setPhoto({ file, key: crypto.randomUUID() });
            }}
          />
          {photo !== null &&
          photoFraction === null &&
          !attachToNew.isPending ? (
            <span className={styles.muted}>{photo.file.name}</span>
          ) : null}
          {photoFraction !== null ? (
            <span className={styles.muted}>
              {t("attachment.uploading", {
                percent: Math.round(photoFraction * 100),
              })}
            </span>
          ) : null}
          {created !== null ? (
            <InlineError variant="block">{t("create.photoFailed")}</InlineError>
          ) : null}
          {attachToNew.isError ? (
            <InlineError>
              {attachToNew.error instanceof UploadError
                ? (() => {
                    const key = attachFailureKey(attachToNew.error.failure);
                    return key === "attachment.rejected" &&
                      attachToNew.error.reason !== null
                      ? t(key, { reason: attachToNew.error.reason })
                      : t(
                          key === "attachment.rejected"
                            ? "attachment.failed"
                            : key,
                        );
                  })()
                : (detailOf(attachToNew.error) ??
                  t(taskErrorKey(attachToNew.error)))}
            </InlineError>
          ) : null}
        </div>

        <div className={styles.field}>
          <Select
            label={t("filter.priority")}
            value={priority}
            disabled={locked}
            onChange={(event) => {
              setPriority(event.target.value as TaskPriority);
            }}
          >
            {TASK_PRIORITIES.map((value) => (
              <option key={value} value={value}>
                {t(PRIORITY_KEY[value])}
              </option>
            ))}
          </Select>
        </div>

        <div className={styles.field}>
          <Select
            label={t("filter.assignee")}
            value={assignee}
            disabled={locked}
            onChange={(event) => {
              setAssignee(event.target.value);
            }}
          >
            <option value="">{t("filter.assigneeUnassigned")}</option>
            {(members.data?.items ?? []).map((row) => (
              <option key={row.user_id} value={row.user_id}>
                {row.display_name ?? row.email ?? t("person.unavailable")}
              </option>
            ))}
          </Select>
        </div>

        {admission !== null ? (
          <div className={styles.notice}>
            <p className={styles.noticeText}>
              {t("assign.admits", {
                name: admission.personName,
                firm: admission.firmName,
              })}
            </p>
          </div>
        ) : null}

        <label className={styles.field}>
          <span className={styles.fieldLabel}>{t("detail.due")}</span>
          <input
            type="date"
            className={styles.select}
            value={dueDay}
            disabled={locked}
            onChange={(event) => {
              setDueDay(event.target.value);
            }}
          />
        </label>

        {/* `unknownLabel` is the pre-filled node the cached tree may not hold: the slice came
            from the rail, which is a link somebody may have opened long after the branch was
            removed. The value stays chosen and goes to the server, whose answer is the real
            one. */}
        <TreePicker
          label={t("under.label")}
          nodes={options}
          value={nodeId === "" ? null : nodeId}
          onChange={(id) => {
            relocate(id ?? "");
          }}
          rowLabel={(option) =>
            option.drawing
              ? t("create.location.drawing", { name: option.name })
              : option.name
          }
          noneLabel={t("create.location.none")}
          unknownLabel={t("under.unknown")}
          disabledAll={locked}
        />

        {drawing ? (
          <fieldset className={styles.anchorFields}>
            <legend className={styles.fieldLabel}>
              {t("create.anchor.legend")}
            </legend>
            <p className={styles.muted}>{t("create.anchor.hint")}</p>
            <div className={styles.anchorRow}>
              <label className={styles.field}>
                <span className={styles.fieldLabel}>
                  {t("create.anchor.x")}
                </span>
                <input
                  type="number"
                  className={styles.select}
                  min={0}
                  max={100}
                  step={1}
                  value={anchorX}
                  disabled={locked}
                  onChange={(event) => {
                    setAnchorX(event.target.value);
                  }}
                />
              </label>
              <label className={styles.field}>
                <span className={styles.fieldLabel}>
                  {t("create.anchor.y")}
                </span>
                <input
                  type="number"
                  className={styles.select}
                  min={0}
                  max={100}
                  step={1}
                  value={anchorY}
                  disabled={locked}
                  onChange={(event) => {
                    setAnchorY(event.target.value);
                  }}
                />
              </label>
              {/* 1-based here and zero-based on the wire — see `pageIndex`. Left empty it is
                  the first page, which is the whole of a single-page drawing. */}
              <label className={styles.field}>
                <span className={styles.fieldLabel}>
                  {t("create.anchor.page")}
                </span>
                <input
                  type="number"
                  className={styles.select}
                  min={1}
                  step={1}
                  value={anchorPage}
                  disabled={locked}
                  placeholder="1"
                  onChange={(event) => {
                    setAnchorPage(event.target.value);
                  }}
                />
              </label>
            </div>
          </fieldset>
        ) : null}

        <LabelPicker
          projectId={projectId}
          labels={labels}
          onChange={setLabels}
        />

        <label className={styles.checkboxRow}>
          <input
            type="checkbox"
            checked={verification}
            // Turnable on, never off, under a type that requires it (§4). Sending `false`
            // would quietly cancel a studio's own "somebody has to confirm this" rule.
            disabled={locked || verificationForced}
            onChange={(event) => {
              setVerification(event.target.checked);
            }}
          />
          <span>
            {t("create.verification")}
            {verificationForced ? ` ${t("create.verificationByType")}` : ""}
          </span>
        </label>

        {/* The panel's own submit, so Enter in a field raises the task. The visible button
            lives in the drawer footer, outside this form. */}
        <button type="submit" className="sr-only" disabled={busy}>
          {t("create.submit")}
        </button>
      </form>
    </Drawer>
  );
}
